import { arrayOf, bool, func, number, object, shape, string } from "prop-types";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";

import {
  AddressMain,
  CountryField,
  FnrAddress,
  Line1,
  Line2,
} from "UI/FnrAddress";
import {
  AddressAutocompleteRoot,
  AddressCallouts,
  AutocompleteCityField,
  AutocompleteStateField,
  AutocompleteStreetField,
  AutocompleteZipField,
  CountryFlagCorner,
  useAddressAutocomplete,
} from "UI/FnrAddressAutocomplete";
import FnrButton from "UI/FnrButton3";

import { countryCodeFromIDOrCode } from "Utilities/Address/networkCountryOptions";

import OtherNetworks from "./OtherNetworks";
import saveIIGAddress from "./api/fetch";

// Subfields whose edits mark the address dirty (and re-engage autocomplete).
const DIRTY_SUBFIELDS = ["addr1", "city", "state", "zip"];

// Card framing. The inner boxes (address field group, other-networks panel)
// carry NEST_RADIUS; the legacy panel gets symmetric PANEL_PADDING and a radius
// of PANEL_PADDING + NEST_RADIUS so the inner corners sit concentrically inside
// the panel corners (uniform gap all the way around). The field group's radius
// is 8px from the FnrAddress kit, so NEST_RADIUS matches it.
const PANEL_PADDING = 16;
const NEST_RADIUS = 8;

IIGAddressCard.propTypes = {
  userID: number.isRequired,
  iigRequestID: number,
  networkFieldID: number.isRequired,
  fieldTypeInstanceID: number.isRequired,
  // The saved `response` from the bootstrap (may be empty/undefined).
  initialValue: object,
  countryVisible: bool,
  otherNetworks: arrayOf(shape({ ID: number, name: string, icon: string })),
  // Injected so the navigate-away redirect is testable. Defaults to the
  // legacy post-save redirect to the member's profile #info.
  onSaved: func,
};

/**
 * React-owned Address card for the IIG single-field page. Collapse-in-place
 * (this surface navigates away on save, so there is no hold and no
 * useStandardizedSave): the autocomplete hook writes committed values straight
 * into local state, and callouts are live. Owns its own Save button and the
 * cross-network sync picker, and POSTs the whole response itself.
 */
export default function IIGAddressCard({
  userID,
  iigRequestID,
  networkFieldID,
  fieldTypeInstanceID,
  initialValue,
  countryVisible,
  otherNetworks = [],
  onSaved,
}) {
  const [value, setValue] = useState(() => ({
    addr1: initialValue?.addr1 || "",
    city: initialValue?.city || "",
    state: initialValue?.state || "",
    zip: initialValue?.zip || "",
    plus4: initialValue?.plus4 || "",
    // The hook speaks ISO and gates US-only verification on `country === "US"`;
    // the stored value may be an ISO code or a numeric ID, so normalize.
    country: countryCodeFromIDOrCode(initialValue?.country),
    isVerified: initialValue?.isVerified === true,
    // Read the provider/verifiedAt back so `uspsVerified` (gated on
    // verificationProvider === "smarty") can light the badge, and so the hook
    // can round-trip them on re-verify.
    verificationProvider: initialValue?.verificationProvider || "",
    verifiedAt: initialValue?.verifiedAt || "",
    validationSkipped: initialValue?.validationSkipped || "",
  }));

  const initialRef = useRef(value);
  const addressIsDirty = DIRTY_SUBFIELDS.some(
    (f) => (value[f] || "") !== (initialRef.current[f] || "")
  );

  // A ref mirror of the value, kept fresh synchronously. Clicking Save first
  // blurs the street input, which commits a live ghost via onChange — but that
  // setValue lands on the NEXT render, after this click's handleSave has already
  // read its closed-over `value`. Reading the ref instead lets handleSave see
  // the just-committed street (the completion the user was looking at) rather
  // than the pre-commit typed fragment. (RHF surfaces like Sacrament get this
  // for free because their form store is the source of truth; plain-state
  // surfaces have to carry the ref themselves.)
  const valueRef = useRef(value);
  valueRef.current = value;

  const onChange = useCallback((delta) => {
    valueRef.current = { ...valueRef.current, ...delta };
    setValue((prev) => ({ ...prev, ...delta }));
  }, []);

  const containerRef = useRef(null);
  const streetRef = useRef(null);
  const cityRef = useRef(null);
  const zipRef = useRef(null);

  // The legacy IIG panel frames this card with padding (40/15) and an 18px
  // radius tuned for the old widget; its own header/photo/buttons are hidden, so
  // for the React card the panel is just the card frame. Give it symmetric
  // padding and a radius that nests the inner boxes concentrically (see the
  // NEST_RADIUS/PANEL_PADDING note above). Restore on unmount so a later
  // non-Address render of this shared page keeps the legacy framing.
  const wrapperRef = useRef(null);
  useLayoutEffect(() => {
    const panel = wrapperRef.current?.closest(".network_subscribe_around");
    if (!panel) return undefined;
    const prev = {
      padding: panel.style.padding,
      borderRadius: panel.style.borderRadius,
    };
    // `important` overrides a legacy responsive `!important` on the panel's
    // horizontal padding, so all four sides land on exactly PANEL_PADDING.
    panel.style.setProperty("padding", `${PANEL_PADDING}px`, "important");
    panel.style.setProperty(
      "border-radius",
      `${PANEL_PADDING + NEST_RADIUS}px`,
      "important"
    );
    return () => {
      if (prev.padding) panel.style.setProperty("padding", prev.padding);
      else panel.style.removeProperty("padding");
      if (prev.borderRadius)
        panel.style.setProperty("border-radius", prev.borderRadius);
      else panel.style.removeProperty("border-radius");
    };
  }, []);

  const autocomplete = useAddressAutocomplete({
    value,
    onChange,
    addr1Ref: streetRef,
    cityRef,
    zipRef,
    containerRef,
    isDirty: addressIsDirty,
    verifyDeliverability: true,
  });
  const {
    liveUnverifiable,
    isUnverifiable,
    standardization,
    onUndoStandardization,
    commitVisibleGhosts,
  } = autocomplete;

  const [share, setShare] = useState(otherNetworks.length > 0);
  const [selectedNetworkIDs, setSelectedNetworkIDs] = useState(() =>
    otherNetworks.map((n) => n.ID)
  );

  const [saving, setSaving] = useState(false);

  const defaultRedirect = useMemo(
    () => () => window.location.assign(`/user/${userID}#info`),
    [userID]
  );

  const handleSave = () => {
    if (saving) return;
    // Freeze the visible ghost (the completion the user is looking at) into the
    // value before reading it — same as the keyboard/hold surfaces. A Save
    // clicked while a forward-completion ghost is still showing then persists
    // what was on screen, not the pre-commit typed fragment. onChange updates
    // valueRef synchronously, so the frozen value is readable on the next line.
    commitVisibleGhosts();
    setSaving(true);
    const shareWithOtherNetworks = share && otherNetworks.length > 0;
    const selectedNetworks = shareWithOtherNetworks
      ? otherNetworks.filter((n) => selectedNetworkIDs.includes(n.ID))
      : undefined;

    saveIIGAddress({
      userID,
      address: valueRef.current,
      networkFieldID,
      fieldTypeInstanceID,
      iigRequestID,
      shareWithOtherNetworks,
      selectedNetworks,
    })
      .then(() => (onSaved || defaultRedirect)())
      .catch(() => setSaving(false));
  };

  return (
    <Wrapper ref={wrapperRef}>
      <AddressAutocompleteRoot ref={containerRef} value={autocomplete}>
        <FnrAddress>
          <AddressMain>
            <StyledLine1 $reserveFlag={countryVisible}>
              <AutocompleteStreetField
                inputStyle={streetStyle}
                acceptRightOffset={countryVisible ? 44 : undefined}
              />
              {countryVisible && (
                <CountryFlagCorner>
                  <CountryField
                    value={value.country}
                    onChange={(id) =>
                      onChange({ country: countryCodeFromIDOrCode(id) })
                    }
                    label=""
                  />
                </CountryFlagCorner>
              )}
            </StyledLine1>
            <Line2>
              <AutocompleteCityField />
              <AutocompleteStateField countryIDOrCode={value.country} />
              <ZipWrapper>
                <AutocompleteZipField countryID={value.country} />
              </ZipWrapper>
            </Line2>
          </AddressMain>
        </FnrAddress>
      </AddressAutocompleteRoot>

      <AddressCallouts
        standardizationOpen={Boolean(standardization)}
        unverifiableOpen={liveUnverifiable || isUnverifiable}
        onUndo={onUndoStandardization}
      />

      {otherNetworks.length > 0 && (
        <OtherNetworks
          networks={otherNetworks}
          value={selectedNetworkIDs}
          onChange={setSelectedNetworkIDs}
          share={share}
          onShareChange={setShare}
        />
      )}

      <SaveRow>
        <FnrButton
          color="blue"
          onClick={handleSave}
          loading={saving}
          aria-label="save-address"
        >
          Save
        </FnrButton>
      </SaveRow>
    </Wrapper>
  );
}

// The panel (adjusted in the layout effect above) supplies the symmetric inset,
// so the content fills the panel's content box; the field group, other-networks
// panel, and Save button all align to that edge.
const Wrapper = styled.div``;

// Line1's 6px padding moves onto the street field wrapper so the nested country
// flag can sit inside the input's right corner; the 36px corner reservation
// lives on the INPUT itself (StyledLine1's input rule below).
const streetStyle = { padding: "6px" };

// Reserve the 36px right gutter on the input only when the country flag nests
// there, so a country-hidden field doesn't carry dead space on its right.
const StyledLine1 = styled(Line1)`
  position: relative;
  padding: 0;
  input {
    padding-right: ${(p) => (p.$reserveFlag ? "36px" : "6px")};
  }
`;

const ZipWrapper = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 80px;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;
